import { Database, supabase } from './supabase'

type CommentRow = Database['public']['Tables']['card_comments']['Row']
type CommentInsert = Database['public']['Tables']['card_comments']['Insert']
type DeleteColumnArgs = Database['public']['Functions']['delete_board_column']['Args']
type DeleteColumnResult = Database['public']['Functions']['delete_board_column']['Returns']

export type ColumnKind = 'stage' | 'auto' | 'parked'

export type Board = {
  id: string
  key: string
  name: string
  description: string | null
  sortOrder: number
  paymentDueThresholdSessions: number
}

export type BoardColumn = {
  id: string
  boardId: string
  key: string
  name: string
  sortOrder: number
  kind: ColumnKind
  requiredFields: CardFieldKey[]
  entryFields: CardFieldKey[]
  stuckAfterHours: number | null
  helpText: string | null
  isSystem: boolean
}

export type BoardCard = {
  id: string
  boardId: string
  columnId: string
  position: number
  columnEnteredAt: string
  parentName: string | null
  phone: string | null
  janeNumber: string | null
  email: string | null
  channel: string | null
  studentFirstName: string | null
  grade: string | null
  subjects: string | null
  mode: string | null
  area: string | null
  source: string | null
  preferredTimes: string | null
  wantedStartDate: string | null
  tutorId: string | null
  rate: string | null
  familyTold: boolean
  paymentMethod: string | null
  studentId: string | null
  pauseReason: string | null
  closeReason: string | null
  closeReasonOther: string | null
  isUnreviewed: boolean
  hasNewLog: boolean
  createdAt: string
  updatedAt: string
}

export type CardEvent = {
  id: string
  cardId: string
  kind: string
  summary: string
  actorEmail: string | null
  createdAt: string
}

export type CardNote = {
  id: string
  cardId: string
  body: string
  authorName: string | null
  createdAt: string
}

export type CardComment = {
  id: string
  cardId: string
  body: string
  authorEmail: string | null
  createdAt: string
}

// Longest comment, in characters. The card_comments table checks the same limit
// (migration 20261030150000): change both together.
export const COMMENT_MAX_LENGTH = 4000

export type CardTask = {
  id: string
  cardId: string
  title: string
  assigneeId: string | null
  dueDate: string | null
  done: boolean
  doneAt: string | null
  autoKey: string | null
  createdAt: string
}

export type CardMessage = {
  id: string
  cardId: string
  channel: 'email' | 'sms'
  toAddress: string
  subject: string | null
  body: string
  reason: string
  status: 'queued' | 'sent' | 'cancelled'
  sentAt: string | null
  createdAt: string
}

export type CardLog = {
  id: string
  cardId: string
  // 'registered': they signed up on the website (the sign-up webhook logs it; staff can too).
  channel: 'call' | 'whatsapp' | 'text' | 'email' | 'registered'
  direction: 'in' | 'out'
  outcome: string
  noAnswer: boolean
  followUpDate: string | null
  note: string | null
  // Texts from Quo: what the family wrote, and links to any attachments.
  message: string | null
  media: LogMedia[]
  createdAt: string
}

export type LogMedia = { url: string; type: string | null }

export type CardFieldKey =
  | 'parentName' | 'phone' | 'email' | 'channel' | 'janeNumber'
  | 'studentFirstName' | 'grade' | 'subjects' | 'mode' | 'area' | 'source' | 'preferredTimes' | 'wantedStartDate'
  | 'tutorId' | 'rate'
  | 'familyTold' | 'paymentMethod'
  | 'pauseReason' | 'closeReason'

export type Option = { value: string; label: string }

export type FieldDef = {
  key: CardFieldKey
  column: string
  label: string
  type: 'text' | 'tel' | 'email' | 'date' | 'select' | 'checkbox' | 'tutor' | 'textarea'
  options?: Option[]
  placeholder?: string
}

export const CHANNEL_OPTIONS: Option[] = [
  { value: 'call', label: 'Phone call' },
  { value: 'text', label: 'Text' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'webform', label: 'Web form' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'other', label: 'Other' },
]

export const MODE_OPTIONS: Option[] = [
  { value: 'in_person', label: 'In person' },
  { value: 'online', label: 'Online' },
  { value: 'savanna_group', label: 'Savanna group' },
]

export const RATE_OPTIONS: Option[] = [
  { value: 'hourly_35', label: '$35 / hour' },
  { value: 'hourly_50', label: '$50 / hour' },
  { value: 'hourly_65', label: '$65 / hour' },
  { value: 'group_300_4wk', label: '$300 / 4 weeks (group)' },
]

export const PAYMENT_METHOD_OPTIONS: Option[] = [
  { value: 'etransfer', label: 'E-transfer (billing@mytutorcalgary.ca)' },
  { value: 'card_wave', label: 'Card via Wave' },
  { value: 'clinic', label: 'Clinic-funded' },
]

export const CLOSE_REASON_OPTIONS: Option[] = [
  { value: 'price', label: 'Price' },
  { value: 'found_elsewhere', label: 'Found elsewhere' },
  { value: 'no_tutor', label: 'No tutor available' },
  { value: 'no_reply_3_tries', label: 'No reply after 3 tries' },
  { value: 'not_a_fit', label: 'Not a fit' },
  { value: 'other', label: 'Other' },
]

export const TUTOR_CLOSE_REASON_OPTIONS: Option[] = [
  { value: 'not_a_fit', label: 'Not a fit' },
  { value: 'withdrew', label: 'Withdrew' },
  { value: 'stopped_tutoring', label: 'Stopped tutoring' },
  { value: 'no_reply_3_tries', label: 'No reply after 3 tries' },
  { value: 'other', label: 'Other' },
]

export const LOG_CHANNEL_OPTIONS: Option[] = [
  { value: 'call', label: 'Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'registered', label: 'Registered user' },
]

export const NOTES_WARNING = 'Tutoring details only. No diagnosis, health or behaviour notes.'

export const FIELD_DEFS: Record<CardFieldKey, FieldDef> = {
  parentName: { key: 'parentName', column: 'parent_name', label: 'Parent name', type: 'text' },
  phone: { key: 'phone', column: 'phone', label: 'Phone', type: 'tel', placeholder: '403 555 0123' },
  email: { key: 'email', column: 'email', label: 'Email', type: 'email' },
  channel: { key: 'channel', column: 'channel', label: 'Came in by', type: 'select', options: CHANNEL_OPTIONS },
  janeNumber: { key: 'janeNumber', column: 'jane_number', label: 'Jane # (clinic)', type: 'text', placeholder: 'e.g. 12' },
  studentFirstName: { key: 'studentFirstName', column: 'student_first_name', label: 'Student first name', type: 'text' },
  grade: { key: 'grade', column: 'grade', label: 'Grade', type: 'text', placeholder: 'e.g. 7' },
  subjects: { key: 'subjects', column: 'subjects', label: 'Subjects', type: 'text', placeholder: 'e.g. Math, Science' },
  mode: { key: 'mode', column: 'mode', label: 'Mode', type: 'select', options: MODE_OPTIONS },
  area: { key: 'area', column: 'area', label: 'Area', type: 'text', placeholder: 'e.g. NW Calgary' },
  source: { key: 'source', column: 'source', label: 'How they heard of us', type: 'text', placeholder: 'e.g. Google, referral' },
  preferredTimes: { key: 'preferredTimes', column: 'preferred_times', label: 'Preferred times', type: 'text', placeholder: 'e.g. Weekdays after 4pm' },
  wantedStartDate: { key: 'wantedStartDate', column: 'wanted_start_date', label: 'Wanted start date', type: 'date' },
  tutorId: { key: 'tutorId', column: 'tutor_id', label: 'Tutor', type: 'tutor' },
  rate: { key: 'rate', column: 'rate', label: 'Rate', type: 'select', options: RATE_OPTIONS },
  familyTold: { key: 'familyTold', column: 'family_told', label: 'Family has been told about the match', type: 'checkbox' },
  paymentMethod: { key: 'paymentMethod', column: 'payment_method', label: 'Payment method', type: 'select', options: PAYMENT_METHOD_OPTIONS },
  pauseReason: { key: 'pauseReason', column: 'pause_reason', label: 'Why is it paused?', type: 'textarea' },
  closeReason: { key: 'closeReason', column: 'close_reason', label: 'Why is it closed?', type: 'select', options: CLOSE_REASON_OPTIONS },
}

export const isCardFieldKey = (key: string): key is CardFieldKey => Object.prototype.hasOwnProperty.call(FIELD_DEFS, key)

export type DetailSection = { title: string; fields: CardFieldKey[] }

export const DETAIL_SECTIONS: DetailSection[] = [
  { title: 'Family', fields: ['parentName', 'phone', 'email', 'channel', 'janeNumber'] },
  { title: 'Student', fields: ['studentFirstName', 'grade', 'subjects', 'mode', 'area', 'source', 'preferredTimes', 'wantedStartDate'] },
  { title: 'Match', fields: ['tutorId', 'rate', 'familyTold', 'paymentMethod'] },
]

export const REQUIRABLE_FIELDS: CardFieldKey[] = (Object.keys(FIELD_DEFS) as CardFieldKey[])
  .filter(k => k !== 'pauseReason' && k !== 'closeReason')

// What a board's cards are about, and how the app shows them. Every board holds families
// except the Tutors board; anything that differs between the two belongs here.
export type BoardProfile = {
  sections: DetailSection[]
  // Fields a column can require (Board settings).
  requirableFields: CardFieldKey[]
  // Labels and options that differ from FIELD_DEFS.
  fieldOverrides: Partial<Record<CardFieldKey, Partial<Pick<FieldDef, 'label' | 'options'>>>>
  // The New enquiry button and window.
  newCardTitle: string
  hasPaymentThreshold: boolean
}

const FAMILY_PROFILE: BoardProfile = {
  sections: DETAIL_SECTIONS,
  requirableFields: REQUIRABLE_FIELDS,
  fieldOverrides: {},
  newCardTitle: 'New enquiry',
  hasPaymentThreshold: true,
}

// The Tutors board's cards are tutors, not families. They use the same fields: the tutor's
// name goes in parent_name, and the student and match fields are left out. Tutors don't pay
// us, so there's no payment threshold.
export const TUTORS_BOARD_KEY = 'tutors'

const TUTOR_SECTIONS: DetailSection[] = [
  { title: 'Tutor', fields: ['parentName', 'phone', 'email', 'channel'] },
  { title: 'Teaching', fields: ['subjects', 'mode', 'area', 'preferredTimes', 'source'] },
]

const TUTOR_PROFILE: BoardProfile = {
  sections: TUTOR_SECTIONS,
  requirableFields: TUTOR_SECTIONS.flatMap(s => s.fields),
  fieldOverrides: { parentName: { label: 'Tutor name' }, closeReason: { options: TUTOR_CLOSE_REASON_OPTIONS } },
  newCardTitle: 'New tutor',
  hasPaymentThreshold: false,
}

export function boardProfile(boardKey: string | undefined): BoardProfile {
  return boardKey === TUTORS_BOARD_KEY ? TUTOR_PROFILE : FAMILY_PROFILE
}

export function fieldDef(key: CardFieldKey, boardKey: string | undefined): FieldDef {
  const override = boardProfile(boardKey).fieldOverrides[key]
  return override ? { ...FIELD_DEFS[key], ...override } : FIELD_DEFS[key]
}

const COLUMN_TO_FIELD: Record<string, CardFieldKey> = Object.fromEntries(
  Object.values(FIELD_DEFS).map(f => [f.column, f.key])
) as Record<string, CardFieldKey>

export function optionLabel(options: Option[] | undefined, value: string | null | undefined): string {
  if (!value) return ''
  return options?.find(o => o.value === value)?.label ?? value
}

export function isFieldFilled(card: Partial<BoardCard>, key: CardFieldKey): boolean {
  const v = card[key]
  if (typeof v === 'boolean') return v
  return v !== null && v !== undefined && String(v).trim() !== ''
}

export function fieldsNeededFor(target: BoardColumn, columns: BoardColumn[]): CardFieldKey[] {
  const needed: CardFieldKey[] = []
  if (target.kind !== 'parked') {
    for (const col of [...columns].sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (col.sortOrder >= target.sortOrder) break
      if (col.kind === 'parked') continue
      for (const f of col.requiredFields) if (!needed.includes(f)) needed.push(f)
    }
  }
  for (const f of target.entryFields) if (!needed.includes(f)) needed.push(f)
  return needed
}

export function missingFieldsFor(card: Partial<BoardCard>, target: BoardColumn, columns: BoardColumn[]): CardFieldKey[] {
  return fieldsNeededFor(target, columns).filter(f => !isFieldFilled(card, f))
}

export function hoursInColumn(card: Pick<BoardCard, 'columnEnteredAt'>, now: Date = new Date()): number {
  return (now.getTime() - new Date(card.columnEnteredAt).getTime()) / 3_600_000
}

export function isStuck(card: Pick<BoardCard, 'columnEnteredAt'>, column: Pick<BoardColumn, 'stuckAfterHours'>, now: Date = new Date()): boolean {
  return column.stuckAfterHours != null && hoursInColumn(card, now) >= column.stuckAfterHours
}

export const NO_REPLY_CLOSE_AFTER = 3

export function mapBoard(r: any): Board {
  return {
    id: r.id, key: r.key, name: r.name, description: r.description, sortOrder: r.sort_order,
    paymentDueThresholdSessions: r.payment_due_threshold_sessions ?? 2,
  }
}

// Where new cards start: the built-in New enquiry column, found by its key wherever it sits,
// else the board's first column. Not "the first column": that can be any column a super admin adds.
export function entryColumn<T extends Pick<BoardColumn, 'key'>>(columns: T[]): T | undefined {
  return columns.find(c => c.key === 'new_enquiry') ?? columns[0]
}

export function mapColumn(r: any): BoardColumn {
  const toKeys = (cols: string[] | null): CardFieldKey[] =>
    (cols ?? []).map(c => COLUMN_TO_FIELD[c]).filter(Boolean)
  return {
    id: r.id,
    boardId: r.board_id,
    key: r.key,
    name: r.name,
    sortOrder: r.sort_order,
    kind: r.kind,
    requiredFields: toKeys(r.required_fields),
    entryFields: toKeys(r.entry_fields),
    stuckAfterHours: r.stuck_after_hours,
    helpText: r.help_text,
    isSystem: !!r.is_system,
  }
}

export function mapCard(r: any): BoardCard {
  return {
    id: r.id,
    boardId: r.board_id,
    columnId: r.column_id,
    position: r.position,
    columnEnteredAt: r.column_entered_at,
    parentName: r.parent_name,
    phone: r.phone,
    janeNumber: r.jane_number ?? null,
    email: r.email,
    channel: r.channel,
    studentFirstName: r.student_first_name,
    grade: r.grade,
    subjects: r.subjects,
    mode: r.mode,
    area: r.area,
    source: r.source,
    preferredTimes: r.preferred_times,
    wantedStartDate: r.wanted_start_date,
    tutorId: r.tutor_id,
    rate: r.rate,
    familyTold: !!r.family_told,
    paymentMethod: r.payment_method,
    studentId: r.student_id,
    pauseReason: r.pause_reason,
    closeReason: r.close_reason,
    closeReasonOther: r.close_reason_other,
    isUnreviewed: !!r.is_unreviewed,
    hasNewLog: !!r.has_new_log,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  }
}

export function toRowPatch(patch: Partial<BoardCard>): Record<string, any> {
  const extra: Partial<Record<keyof BoardCard, string>> = {
    boardId: 'board_id',
    columnId: 'column_id',
    position: 'position',
    studentId: 'student_id',
    closeReasonOther: 'close_reason_other',
    isUnreviewed: 'is_unreviewed',
    hasNewLog: 'has_new_log',
  }
  const row: Record<string, any> = {}
  for (const [k, v] of Object.entries(patch)) {
    const col = (FIELD_DEFS as any)[k]?.column ?? extra[k as keyof BoardCard]
    if (!col) continue
    row[col] = typeof v === 'string' && v.trim() === '' ? null : v
  }
  return row
}

export function cardTitle(
  card: Pick<BoardCard, 'parentName' | 'studentFirstName' | 'phone'> & Partial<Pick<BoardCard, 'janeNumber'>>
): string {
  const family = card.parentName?.trim()
  const student = card.studentFirstName?.trim()
  const jane = card.janeNumber?.trim()
  if (family && student) return `${student} (${family})`
  return student || family || card.phone || (jane ? `Jane #${jane}` : 'New enquiry')
}

export async function fetchBoards(): Promise<{ boards: Board[]; columns: BoardColumn[] }> {
  const [b, c] = await Promise.all([
    supabase.from('boards').select('*').eq('is_active', true).order('sort_order'),
    supabase.from('board_columns').select('*').order('sort_order'),
  ])
  if (b.error) throw b.error
  if (c.error) throw c.error
  return { boards: (b.data ?? []).map(mapBoard), columns: (c.data ?? []).map(mapColumn) }
}

const PAGE_SIZE = 1000
const ID_CHUNK = 150

async function fetchAllPages<T>(page: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>): Promise<T[]> {
  const rows: T[] = []
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await page(from, from + PAGE_SIZE - 1)
    if (error) throw error
    rows.push(...(data ?? []))
    if (!data || data.length < PAGE_SIZE) return rows
  }
}

async function countPerCard(
  cardIds: string[],
  query: (ids: string[], from: number, to: number) => PromiseLike<{ data: { card_id: string }[] | null; error: any }>
): Promise<Record<string, number>> {
  const chunks: string[][] = []
  for (let i = 0; i < cardIds.length; i += ID_CHUNK) chunks.push(cardIds.slice(i, i + ID_CHUNK))
  const results = await Promise.all(chunks.map(ids => fetchAllPages((from, to) => query(ids, from, to))))
  const counts: Record<string, number> = {}
  for (const r of results.flat()) counts[r.card_id] = (counts[r.card_id] ?? 0) + 1
  return counts
}

export async function fetchCards(boardId: string): Promise<BoardCard[]> {
  const rows = await fetchAllPages((from, to) =>
    supabase
      .from('board_cards')
      .select('*')
      .eq('board_id', boardId)
      .order('position')
      .order('id')
      .range(from, to)
  )
  return rows.map(mapCard)
}

export async function nextPositionInColumn(columnId: string): Promise<number> {
  const { data, error } = await supabase
    .from('board_cards')
    .select('position')
    .eq('column_id', columnId)
    .order('position', { ascending: false })
    .limit(1)
  if (error) throw error
  return data && data.length > 0 ? data[0].position + 1 : 0
}

// Where a new card goes: on top of the column, which lists cards lowest position first.
// The webhooks' database functions do the same.
export async function topPositionInColumn(columnId: string): Promise<number> {
  const { data, error } = await supabase
    .from('board_cards')
    .select('position')
    .eq('column_id', columnId)
    .order('position', { ascending: true })
    .limit(1)
  if (error) throw error
  return data && data.length > 0 ? data[0].position - 1 : 0
}

export async function updateCard(cardId: string, patch: Partial<BoardCard>): Promise<BoardCard> {
  const { data, error } = await supabase
    .from('board_cards')
    .update(toRowPatch(patch))
    .eq('id', cardId)
    .select('*')
    .single()
  if (error) throw error
  return mapCard(data)
}

// Clears the "New" and "New log" flags as the database has them, not as the board's copy does.
// Returns null when neither was set.
export async function markCardSeen(cardId: string): Promise<{ card: BoardCard; wasUnreviewed: boolean } | null> {
  const reviewed = await supabase
    .from('board_cards')
    .update({ is_unreviewed: false, has_new_log: false })
    .eq('id', cardId)
    .eq('is_unreviewed', true)
    .select('*')
  if (reviewed.error) throw reviewed.error
  if (reviewed.data.length) return { card: mapCard(reviewed.data[0]), wasUnreviewed: true }

  const seen = await supabase
    .from('board_cards')
    .update({ has_new_log: false })
    .eq('id', cardId)
    .eq('has_new_log', true)
    .select('*')
  if (seen.error) throw seen.error
  return seen.data.length ? { card: mapCard(seen.data[0]), wasUnreviewed: false } : null
}

export async function createCard(boardId: string, columnId: string, values: Partial<BoardCard>, position: number): Promise<BoardCard> {
  const { data, error } = await supabase
    .from('board_cards')
    .insert({ ...toRowPatch(values), board_id: boardId, column_id: columnId, position })
    .select('*')
    .single()
  if (error) throw error
  return mapCard(data)
}

export async function deleteCard(cardId: string): Promise<void> {
  const { error } = await supabase.from('board_cards').delete().eq('id', cardId)
  if (error) throw error
}

export async function addCardEvent(cardId: string, kind: 'updated' | 'task' | 'log', summary: string, actorEmail?: string | null) {
  const { error } = await supabase.from('card_events').insert({ card_id: cardId, kind, summary, actor_email: actorEmail ?? null })
  if (error) console.error('addCardEvent failed (non-fatal):', error)
}

export async function fetchCardEvents(cardId: string): Promise<CardEvent[]> {
  const { data, error } = await supabase
    .from('card_events')
    .select('*')
    .eq('card_id', cardId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    id: r.id, cardId: r.card_id, kind: r.kind, summary: r.summary, actorEmail: r.actor_email, createdAt: r.created_at,
  }))
}

export function mapNote(r: any): CardNote {
  return { id: r.id, cardId: r.card_id, body: r.body, authorName: r.author_name, createdAt: r.created_at }
}

export async function fetchCardNotes(cardId: string): Promise<CardNote[]> {
  const { data, error } = await supabase
    .from('card_notes')
    .select('*')
    .eq('card_id', cardId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map(mapNote)
}

export async function createCardNote(cardId: string, body: string, authorName: string | null): Promise<CardNote> {
  const { data, error } = await supabase
    .from('card_notes')
    .insert({ card_id: cardId, body: body.trim(), author_name: authorName })
    .select('*')
    .single()
  if (error) throw error
  return mapNote(data)
}

export async function deleteCardNote(noteId: string): Promise<void> {
  const { error } = await supabase.from('card_notes').delete().eq('id', noteId)
  if (error) throw error
}

export function mapComment(r: CommentRow): CardComment {
  return { id: r.id, cardId: r.card_id, body: r.body, authorEmail: r.author_email ?? null, createdAt: r.created_at }
}

export async function fetchCardComments(cardId: string): Promise<CardComment[]> {
  const { data, error } = await supabase
    .from('card_comments')
    .select('*')
    .eq('card_id', cardId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return ((data ?? []) as CommentRow[]).map(mapComment)
}

// The database fills in the author (who is signed in), so it isn't sent.
export async function createCardComment(cardId: string, body: string): Promise<CardComment> {
  const row: CommentInsert = { card_id: cardId, body: body.trim() }
  const { data, error } = await supabase.from('card_comments').insert(row).select('*').single()
  if (error) throw error
  return mapComment(data as CommentRow)
}

function mapTask(r: any): CardTask {
  return {
    id: r.id, cardId: r.card_id, title: r.title, assigneeId: r.assignee_id,
    dueDate: r.due_date, done: !!r.done, doneAt: r.done_at, autoKey: r.auto_key ?? null, createdAt: r.created_at,
  }
}

export async function fetchCardTasks(cardId: string): Promise<CardTask[]> {
  const { data, error } = await supabase.from('card_tasks').select('*').eq('card_id', cardId).order('created_at')
  if (error) throw error
  return (data ?? []).map(mapTask)
}

export async function createCardTask(cardId: string, t: { title: string; assigneeId: string | null; dueDate: string | null }): Promise<CardTask> {
  const { data, error } = await supabase
    .from('card_tasks')
    .insert({ card_id: cardId, title: t.title, assignee_id: t.assigneeId, due_date: t.dueDate })
    .select('*')
    .single()
  if (error) throw error
  return mapTask(data)
}

export async function setCardTaskDone(taskId: string, done: boolean): Promise<CardTask> {
  const { data, error } = await supabase
    .from('card_tasks')
    .update({ done, done_at: done ? new Date().toISOString() : null })
    .eq('id', taskId)
    .select('*')
    .single()
  if (error) throw error
  return mapTask(data)
}

export async function deleteCardTask(taskId: string): Promise<void> {
  const { error } = await supabase.from('card_tasks').delete().eq('id', taskId)
  if (error) throw error
}

// Attachments are shown as links, so anything but an https link is dropped.
export function mapLogMedia(media: unknown): LogMedia[] {
  if (!Array.isArray(media)) return []
  return media.flatMap((m: any) =>
    typeof m?.url === 'string' && /^https:\/\//i.test(m.url)
      ? [{ url: m.url, type: typeof m.type === 'string' ? m.type : null }]
      : []
  )
}

function mapLog(r: any): CardLog {
  return {
    id: r.id, cardId: r.card_id, channel: r.channel, direction: r.direction, outcome: r.outcome,
    noAnswer: !!r.no_answer, followUpDate: r.follow_up_date, note: r.note,
    message: r.message ?? null, media: mapLogMedia(r.media), createdAt: r.created_at,
  }
}

export async function fetchCardLogs(cardId: string): Promise<CardLog[]> {
  const { data, error } = await supabase
    .from('card_logs')
    .select('*')
    .eq('card_id', cardId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map(mapLog)
}

export async function createCardLog(
  cardId: string,
  l: Omit<CardLog, 'id' | 'cardId' | 'createdAt' | 'message' | 'media'>
): Promise<CardLog> {
  const { data, error } = await supabase
    .from('card_logs')
    .insert({
      card_id: cardId, channel: l.channel, direction: l.direction, outcome: l.outcome,
      no_answer: l.noAnswer, follow_up_date: l.followUpDate, note: l.note,
    })
    .select('*')
    .single()
  if (error) throw error
  return mapLog(data)
}

export async function fetchNoAnswerCounts(cardIds: string[]): Promise<Record<string, number>> {
  return countPerCard(cardIds, (ids, from, to) =>
    supabase.from('card_logs').select('card_id').eq('no_answer', true).in('card_id', ids).order('id').range(from, to)
  )
}

export async function fetchOpenTaskCounts(cardIds: string[]): Promise<Record<string, number>> {
  return countPerCard(cardIds, (ids, from, to) =>
    supabase.from('card_tasks').select('card_id').eq('done', false).in('card_id', ids).order('id').range(from, to)
  )
}

export async function updateBoardThreshold(boardId: string, sessions: number): Promise<void> {
  const { error } = await supabase.from('boards').update({ payment_due_threshold_sessions: sessions }).eq('id', boardId)
  if (error) throw error
}

export async function fetchStudentName(studentId: string): Promise<string | null> {
  const { data, error } = await supabase.from('students').select('name').eq('id', studentId).maybeSingle()
  if (error) throw error
  return data?.name ?? null
}

function mapMessage(r: any): CardMessage {
  return {
    id: r.id, cardId: r.card_id, channel: r.channel, toAddress: r.to_address, subject: r.subject,
    body: r.body, reason: r.reason, status: r.status, sentAt: r.sent_at, createdAt: r.created_at,
  }
}

export async function fetchCardMessages(cardId: string): Promise<CardMessage[]> {
  const { data, error } = await supabase
    .from('card_messages')
    .select('*')
    .eq('card_id', cardId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map(mapMessage)
}

export async function setCardMessageStatus(messageId: string, status: 'sent' | 'cancelled'): Promise<CardMessage> {
  const { data: auth } = await supabase.auth.getUser()
  const { data, error } = await supabase
    .from('card_messages')
    .update({
      status,
      sent_at: status === 'sent' ? new Date().toISOString() : null,
      sent_by: status === 'sent' ? auth.user?.id ?? null : null,
    })
    .eq('id', messageId)
    .select('*')
    .single()
  if (error) throw error
  return mapMessage(data)
}

export async function fetchQueuedMessageCounts(cardIds: string[]): Promise<Record<string, number>> {
  return countPerCard(cardIds, (ids, from, to) =>
    supabase.from('card_messages').select('card_id').eq('status', 'queued').in('card_id', ids).order('id').range(from, to)
  )
}

export function mailtoLink(m: Pick<CardMessage, 'toAddress' | 'subject' | 'body'>): string {
  const params = new URLSearchParams()
  if (m.subject) params.set('subject', m.subject)
  params.set('body', m.body)
  return `mailto:${encodeURIComponent(m.toAddress)}?${params.toString().replace(/\+/g, '%20')}`
}

const toDbFields = (keys: CardFieldKey[]) => keys.map(k => FIELD_DEFS[k].column)

export type ColumnPatch = Partial<Pick<BoardColumn, 'name' | 'helpText' | 'stuckAfterHours' | 'requiredFields' | 'sortOrder'>>

export async function updateColumn(columnId: string, patch: ColumnPatch): Promise<BoardColumn> {
  const row: Record<string, any> = {}
  if (patch.name !== undefined) row.name = patch.name.trim()
  if (patch.helpText !== undefined) row.help_text = patch.helpText?.trim() || null
  if (patch.stuckAfterHours !== undefined) row.stuck_after_hours = patch.stuckAfterHours
  if (patch.requiredFields !== undefined) row.required_fields = toDbFields(patch.requiredFields)
  if (patch.sortOrder !== undefined) row.sort_order = patch.sortOrder
  const { data, error } = await supabase.from('board_columns').update(row).eq('id', columnId).select('*').single()
  if (error) throw error
  return mapColumn(data)
}

export function columnKeyFromName(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'column'
}

export async function createColumn(boardId: string, name: string, sortOrder: number): Promise<BoardColumn> {
  const { data, error } = await supabase
    .from('board_columns')
    .insert({
      board_id: boardId,
      key: `${columnKeyFromName(name)}_${Date.now().toString(36)}`,
      name: name.trim(),
      sort_order: sortOrder,
      kind: 'stage',
    })
    .select('*')
    .single()
  if (error) throw error
  return mapColumn(data)
}

// Deletes the column and every card in it, together or not at all (delete_board_column,
// migration 20261030170000). `expectedCards` is the count the confirmation showed: if the column
// now holds a different number, nothing is deleted and the error's `cardCountChanged` is true.
// Returns how many cards were deleted.
export async function deleteColumn(columnId: string, expectedCards: number): Promise<number> {
  const args: DeleteColumnArgs = { p_column_id: columnId, p_expected_cards: expectedCards }
  const { data, error } = await supabase.rpc('delete_board_column', args)
  if (error) {
    throw Object.assign(new Error(error.message), { cardCountChanged: error.hint === 'card_count_changed' })
  }
  const deleted = data as DeleteColumnResult | null
  return typeof deleted === 'number' ? deleted : 0
}

export function reorderColumns(columns: BoardColumn[], columnId: string, direction: -1 | 1): { id: string; sortOrder: number }[] {
  const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder)
  const i = sorted.findIndex(c => c.id === columnId)
  const j = i + direction
  if (i < 0 || j < 0 || j >= sorted.length) return []
  ;[sorted[i], sorted[j]] = [sorted[j], sorted[i]]
  return sorted
    .map((c, idx) => ({ id: c.id, sortOrder: idx + 1, before: c.sortOrder }))
    .filter(c => c.sortOrder !== c.before)
    .map(({ id, sortOrder }) => ({ id, sortOrder }))
}

export function stuckLabel(hours: number | null): string {
  if (hours == null) return 'never'
  if (hours % 24 === 0) return `${hours / 24} day${hours === 24 ? '' : 's'}`
  return `${hours} hour${hours === 1 ? '' : 's'}`
}
